import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationApiService } from '@warema/shared/authentication/api';
import { AppointmentConnectorService } from '@warema/shared/connector-services';
import { IAppConfig } from '@warema/shared/models';
import { AppointmentTrackingService } from '@warema/shared/services';
import { TenantConfigurationService } from '@warema/shared/tenant-config';
import { BehaviorSubject, filter, first } from 'rxjs';
import { MenuItemType } from '../../models/enums/MenuItemType';
import { Menu } from '../../models/Menu';
import { MenuItem } from '../../models/MenuItem';

@Injectable({
    providedIn: 'root',
})
export class MenuService {
    private readonly _appointmentConnectorService = inject(AppointmentConnectorService);
    private readonly _authenticationApiService = inject(AuthenticationApiService);
    private readonly _router = inject(Router);
    private readonly _trackingService = inject(AppointmentTrackingService);
    private readonly _tenantConfig = inject(TenantConfigurationService);

    public numberOfInProgressAppointments$ = new BehaviorSubject<number>(0);
    public sideMenu$ = new BehaviorSubject<Menu | null>(null);
    public burgerMenus$ = new BehaviorSubject<Array<Menu>>([]);

    public hideSideMenu$ = new BehaviorSubject<boolean>(false);

    constructor() {
        this._tenantConfig.settings$.pipe(filter(v => v != null)).subscribe(settings => {
            this._initSideMenu(settings);
            this._handleUserAuthentication(settings);
        });
    }

    public setNumberOfInProgressAppointments(): void {
        this._appointmentConnectorService
            .getNumberOfInProgressAppointments()
            .pipe(first())
            .subscribe((count: number) => {
                this.numberOfInProgressAppointments$.next(count);
            });
    }

    /**
     * activate menu by style and navigate ('/shop') or run logic (login/logout)
     *
     * @param menuItem clicked menu from header
     * @returns void
     */
    public goToMenuItem(menuItem: MenuItem): void {
        const allMenuItems = this._getAllMenus().flatMap(x => x.menuItems);
        const selectedMenuItems = allMenuItems.filter(x => x.menuItemType === menuItem.menuItemType);
        const selectedMenu = selectedMenuItems[0];

        if (!selectedMenu) {
            return;
        }

        if (selectedMenu.selected) {
            this._executeMenuItemClick(selectedMenu);
            return;
        }
        allMenuItems.forEach(x => (x.selected = false));
        selectedMenuItems.forEach(x => (x.selected = true));
        this._executeMenuItemClick(selectedMenu);
    }

    /**
     * navigate to URL
     *
     * @param menuItem clicked menu from header
     * @returns void
     */
    public goToUrl(url: string, openInNewTab: boolean = false): void {
        if (openInNewTab) {
            window.open(url, '_blank')?.focus();
            return;
        }

        window.location.assign(url);
    }

    private _executeMenuItemClick(menuItem: MenuItem): void {
        if (menuItem.url) {
            void this._router.navigateByUrl(menuItem.url);
        } else if (menuItem.processLogic) {
            menuItem.processLogic();
        }

        if (menuItem.onClickEvent) {
            menuItem.onClickEvent();
        }
    }

    /**
     * search in menu list the best matching menu by URL and set is as ACTIVE (css style)
     *
     * @param url navigation url from browser
     * @returns void
     */
    public activateMenuByUrl(url: string): void {
        let menuItem: MenuItem | null = null;
        const allMenus = this._getAllMenus().flatMap(x => x.menuItems);

        const equalMatched = allMenus
            .filter(x => url === x.url && x.url)
            .sort((a, b) => b.url.length - a.url.length)[0];
        if (equalMatched != null) {
            menuItem = equalMatched;
        } else {
            // best matched, exclude home '/'
            menuItem = allMenus
                .filter(x => url.includes(x.url) && x.menuItemType !== MenuItemType.HOME)
                .sort((a, b) => b.url.length - a.url.length)[0];
        }

        if (menuItem != null) {
            const selectedMenus = allMenus.filter(x => x.menuItemType === menuItem.menuItemType);
            allMenus.forEach(x => (x.selected = false));
            selectedMenus.forEach(x => (x.selected = true));
        }

        if (url.startsWith('/shop')) {
            const sideMenu = this.sideMenu$.getValue();
            const shopMenuItemIndex = sideMenu.menuItems.findIndex(x => x.menuItemType === MenuItemType.SHOP);
            sideMenu.menuItems[shopMenuItemIndex].selected = true;

            this.sideMenu$.next({ ...sideMenu });
        }

        if (
            url.startsWith('/company-search-workflow') ||
            url.startsWith('/appointment-workflow') ||
            url.startsWith('/offer-workflow')
        ) {
            allMenus.forEach(x => (x.selected = false));
            const dealerMenuItemIndex = allMenus.findIndex(x => x.menuItemType === MenuItemType.COMPANY_SEARCH);
            allMenus[dealerMenuItemIndex].selected = true;
        }
    }

    public setActiveMenu(type: MenuItemType): void {
        const allMenus = this._getAllMenus().flatMap(x => x.menuItems);
        const selectedMenus = allMenus.filter(x => x.menuItemType === type);

        allMenus.forEach(x => (x.selected = false));
        selectedMenus.forEach(x => (x.selected = true));
    }

    public trackDealerMenuButton(): void {
        this._trackingService.trackDealerMenuButton();
    }

    private _getAllMenus(): Array<Menu> {
        const sideMenu = this.sideMenu$.getValue();
        const burgerMenus = this.burgerMenus$.getValue();

        return [sideMenu, ...burgerMenus];
    }

    private _handleUserAuthentication(setting: IAppConfig): void {
        this._authenticationApiService.loggedIn$.subscribe(loggedIn => {
            this.hideSideMenu$.next(!loggedIn);
            const menuItems = this._getBurgerMenus(setting, loggedIn);
            this.burgerMenus$.next(menuItems);
        });
    }

    private _initSideMenu(settings: IAppConfig): void {
        const menuItems: Array<MenuItem> = [];

        menuItems.push(new MenuItem('home-menu-item', MenuItemType.HOME, 'HOME', 'home', '/'));

        if (settings.consultations?.enabled) {
            menuItems.push(
                new MenuItem(
                    'consultations-menu-item',
                    MenuItemType.CONSULTATIONS,
                    'CONSULTATIONS',
                    'chat',
                    '/consultations',
                ),
            );
        }

        if (settings.projects?.enabled) {
            menuItems.push(
                new MenuItem('projects-menu-item', MenuItemType.PROJECTS, 'PROJECTS', 'folder', '/projects'),
            );
        }

        if (settings.appointment?.enabled) {
            menuItems.push(
                new MenuItem(
                    'appointments',
                    MenuItemType.APPOINTMENTS,
                    'APPOINTMENTS',
                    'calendar',
                    '/appointments',
                    undefined,
                    undefined,
                    () => {
                        this._trackingService.trackAppointmentMenuButton();
                    },
                ),
            );
        }

        if (settings.devices?.enabled) {
            menuItems.push(
                new MenuItem('devices-menu-item', MenuItemType.DEVICES, 'CONTROLS', 'smartphoneDevices', '/devices'),
            );
        }

        if (settings.productRegistration?.enabled) {
            menuItems.push(
                new MenuItem('products-menu-item', MenuItemType.PRODUCTS, 'PRODUCTS', 'checkedCircle', '/products'),
            );
        }

        if (settings.shop?.enabled) {
            menuItems.push(
                new MenuItem('shop-menu-item', MenuItemType.SHOP, 'SHOP.SHOP', 'cart', '/shop'),
                new MenuItem(
                    'order-history-item',
                    MenuItemType.ORDER_HISTORY,
                    'ORDER_HISTORY.TITLE',
                    'clockTimerLeft',
                    '/order-history',
                ),
            );
        }

        if (settings.account.enabled) {
            menuItems.push(
                new MenuItem(
                    'profile-menu-item',
                    MenuItemType.PROFILE,
                    'MY_PROFILE',
                    'person',
                    undefined,
                    undefined,
                    () => {
                        void this._router.navigate(['/profile'], { queryParams: { section: 'personal-data' } });
                    },
                ),
            );
        }

        this.sideMenu$.next(new Menu('side-menu', menuItems));
    }

    private _getBurgerMenus(settings: IAppConfig, isLoggedIn: boolean): Array<Menu> {
        const menus: Array<Menu> = [];
        const topLevelBurgerMenuItems: Array<MenuItem> = [];
        menus.push(new Menu('burger-menu', topLevelBurgerMenuItems));
        topLevelBurgerMenuItems.push(new MenuItem('home-menu-item', MenuItemType.HOME, 'HOME', 'home', '/'));

        if (settings.consultations.enabled && isLoggedIn) {
            topLevelBurgerMenuItems.push(
                new MenuItem(
                    'consultations-menu-item',
                    MenuItemType.CONSULTATIONS,
                    'CONSULTATIONS',
                    'chat',
                    '/consultations',
                ),
            );
        }

        if (settings.projects?.enabled && isLoggedIn) {
            topLevelBurgerMenuItems.push(
                new MenuItem('projects-menu-item', MenuItemType.PROJECTS, 'PROJECTS', 'folder', '/projects'),
            );
        }

        if (settings.companySearch?.enabled) {
            topLevelBurgerMenuItems.push(
                new MenuItem(
                    'company-search-menu-item',
                    MenuItemType.COMPANY_SEARCH,
                    'DEALER_SEARCH',
                    'location',
                    undefined,
                    undefined,
                    () => {
                        this._trackingService.trackDealerMenuButton();
                        void this._router.navigate(['/company-search-workflow'], {
                            queryParams: { origin: 'WaremaPlusDefault' },
                        });
                    },
                ),
            );
        }

        if (settings.appointment?.enabled && isLoggedIn) {
            topLevelBurgerMenuItems.push(
                new MenuItem(
                    'appointments-menu-item',
                    MenuItemType.APPOINTMENTS,
                    'APPOINTMENTS',
                    'calendar',
                    '/appointments',
                    undefined,
                    null,
                    () => {
                        this._trackingService.trackAppointmentMenuButton();
                    },
                ),
            );
        }

        if (settings.devices.enabled && isLoggedIn) {
            topLevelBurgerMenuItems.push(
                new MenuItem('devices-menu-item', MenuItemType.DEVICES, 'CONTROLS', 'smartphoneDevices', '/devices'),
            );
        }

        if (settings.productRegistration.enabled && isLoggedIn) {
            topLevelBurgerMenuItems.push(
                new MenuItem('products-menu-item', MenuItemType.PRODUCTS, 'PRODUCTS', 'checkedCircle', '/products'),
            );
        }

        if (settings.shop?.enabled) {
            const shopMenuTriggerId = 'shop-menu';

            const shopBurgerMenuItems = [
                new MenuItem('shop-menu-item', MenuItemType.SHOP, 'SHOP.HOME', 'home', '/shop'),
                new MenuItem(
                    'shop-fabrics-menu-item',
                    MenuItemType.SHOP_FABRICS,
                    'SHOP.FABRICS',
                    'fabrics',
                    '/shop/fabrics',
                ),
                new MenuItem(
                    'shop-slats-menu-item',
                    MenuItemType.SHOP_SLATS,
                    'END_CUSTOMER_CONFIG_PROP.slatColour',
                    'slatsTurnedRight',
                    '/shop/slats',
                ),
                new MenuItem(
                    'shop-brochures-menu-item',
                    MenuItemType.SHOP_BROCHURES,
                    'SHOP.BROCHURES',
                    'brochure',
                    '/shop/brochures',
                ),
                new MenuItem('shop-card-menu-item', MenuItemType.SHOP_CART, 'SHOP.CART', 'cart', '/shop/cart'),
            ];
            const shopSubMenu = new Menu(shopMenuTriggerId, shopBurgerMenuItems);
            shopSubMenu.parentMenuId = 'shared-burger-menu';
            menus.push(shopSubMenu);
            topLevelBurgerMenuItems.push(
                new MenuItem(
                    'shop-menu-parent-item',
                    MenuItemType.SHOP,
                    'SHOP.SHOP',
                    'cart',
                    undefined,
                    shopMenuTriggerId,
                ),
                new MenuItem(
                    'order-history-item',
                    MenuItemType.ORDER_HISTORY,
                    'ORDER_HISTORY.TITLE',
                    'clockTimerLeft',
                    '/order-history',
                ),
            );
        }

        if (settings.account?.enabled && isLoggedIn) {
            topLevelBurgerMenuItems.push(
                new MenuItem(
                    'profile-menu-item',
                    MenuItemType.PROFILE,
                    'MY_PROFILE',
                    'person',
                    undefined,
                    undefined,
                    () => {
                        void this._router.navigate(['/profile'], { queryParams: { section: 'personal-data' } });
                    },
                ),
            );
        }

        return menus;
    }
}
